import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import _ from 'underscore';

import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { RouterErrorType } from '~src/bootstrap/redux/state/RouterErrorType';
import { RedirectWithURLSearchParamsWrapper } from '~src/bootstrap/routerHelpers/RedirectWithURLSearchParamsWrapper';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Favorites } from '~src/favorites/resources/Favorites';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Views } from '~src/views/resources/Views';

// This isn't named RedirectToFavoritedViewProvider because we use this redirect for view links everywhere
// (see ViewDefinition's contentSpaceUrl) and in the favorites menu. We want different behaviors based on
// which redirect this is though.
export type RedirectViewType = 'customView' | 'favorite' | 'regular';

const redirectTypeToErrorType = new Map<RedirectViewType, RouterErrorType>([
  ['customView', 'custom-view'],
  ['favorite', 'view-removed-favorite'],
  ['regular', 'view']
]);

export interface RedirectToViewProviderProps {
  readonly viewId: string;
  readonly customViewUrlId?: string;
  readonly urls: SpacesSiteUrls;
  readonly redirectType: RedirectViewType;
  readonly customViewLuid?: string;
}

export interface RedirectToViewSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
}

export interface RedirectToViewProviderState {
  readonly viewPath: string;
  readonly hasError: boolean;
  readonly errorCode: number;
}

const InitialState: RedirectToViewProviderState = {
  viewPath: undefined,
  hasError: false,
  errorCode: undefined
};

export type RedirectToViewSpaceProviderContainerProps = RedirectToViewProviderProps & RedirectToViewSpaceProviderStateProps;

export class RedirectToViewProvider extends React.PureComponent<RedirectToViewSpaceProviderContainerProps, RedirectToViewProviderState> {
  private unmounted: boolean = false;

  public constructor(props: RedirectToViewSpaceProviderContainerProps) {
    super(props);
    this.state = InitialState;
  }

  public render(): JSX.Element {
    if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        if (FeatureFlags.requestAccessDialogEnabled && (this.state.errorCode === CommonErrorCode.CallingUserInsufficientPermissions)) {
          return <Redirect to={`${this.props.urls.homePath()}${this.props.urls.viewRequestAccessPath(this.props.viewId)}`} />;
        } else {
          // always use view (instead of redirectType) as contentType for request access is for the base view
          return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.viewId} contentType={'view'} errorCode={this.state.errorCode} />;
        }
      }
      return <RouterError errorType={redirectTypeToErrorType.get(this.props.redirectType)} urls={this.props.urls} />;
    } else if (!_.isUndefined(this.state.viewPath)) {
      let viewPath = this.state.viewPath;
      if (this.props.redirectType === 'customView') {
        viewPath = `${viewPath}/${this.props.customViewLuid}/${this.props.customViewUrlId}`;
      }

      return <RedirectWithURLSearchParamsWrapper to={this.props.urls.viewPath(viewPath)} />;
    }
    return null;
  }

  public async componentDidMount() {
    if (_.isUndefined(this.props.viewId)) {
      this.setToErrorStateAndRemoveFromFavorites();
      return;
    }

    try {
      const view = await Views.getById(this.props.viewId);
      if (view) {
        this.updateState({ viewPath: view.path }) ;
      } else {
        this.setToErrorStateAndRemoveFromFavorites();
      }

    } catch (e) {
      this.setToErrorStateAndRemoveFromFavorites(e as number);
    }
  }

  private setToErrorStateAndRemoveFromFavorites(errorCode?: number) {
    if (this.props.redirectType === 'favorite') {
      Favorites.removeViewById(this.props.viewId);
    }
    this.updateState({ hasError: true, errorCode, });
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private updateState<K extends keyof RedirectToViewProviderState>(newState: Pick<RedirectToViewProviderState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

const mapStateToProps: MapStateToProps<RedirectToViewSpaceProviderStateProps> = (appState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(appState)
});


export const ConnectedRedirectToViewSpaceProvider: React.ComponentClass<RedirectToViewProviderProps>
  = connect(mapStateToProps)(RedirectToViewProvider);
